<script setup lang="ts">
import { useRoute } from 'vue-router';
import { computed, onMounted } from 'vue';
import type { ComputedRef } from 'vue';
import { useCompanyStore } from '@/stores/company';
import { useAuthStore } from '@/stores/auth';
import { hasFeatureAccess, isCialInternalUser } from '@/utils/auth';
import { useActivityLogger } from '@/composables/useActivityLogger';
import { useEntityMonitoringStatus, useEntityEventCount } from '@/composables/useEntityMonitoring';
import CompanyBanner from '@/components/company/CompanyBanner.vue';
import BaseIcon from '@/components/base/BaseIcon.vue';

const route = useRoute();
const companyStore = useCompanyStore();
const authStore = useAuthStore();
const { logView } = useActivityLogger();

const taxId: ComputedRef<string> = computed(() => (route.params.cnpj as string || '').slice(0, 8));
const isCialUser = computed(() => isCialInternalUser(authStore.user?.email));
const hasFeatureAccessOrIsCialUser = computed(() => isCialUser.value || hasFeatureAccess(authStore.user, 'kyt'))
const hasPulseAccess = computed(() => isCialUser.value || hasFeatureAccess(authStore.user, 'pulse'));

const { view: monitoringView } = useEntityMonitoringStatus({
  entityType: 'company',
  taxId,
  enabled: hasPulseAccess,
});
const {
  eventCount: pulseEventCount,
  isEventCountLoading: pulseEventCountLoading,
} = useEntityEventCount({
  entityType: 'company',
  taxId,
  enabled: hasPulseAccess,
});

async function getDataForTaxId(cnpj8: string): Promise<void> {
  if (!cnpj8) return;

  // 1. Reset if CNPJ changed
  if (companyStore.currentCnpj8 && companyStore.currentCnpj8 !== cnpj8) {
    companyStore.resetGeneralData();
  }

  try {
    // General data: one call handles access-check + auto-acquire + fetch. Skip it if this CNPJ
    // was already attempted — hasGeneralData is false (not null) after a failed attempt too,
    // so this — unlike checking generalData itself — doesn't retry-loop on every remount.
    const alreadyAttempted = companyStore.currentCnpj8 === cnpj8 && companyStore.hasGeneralData !== null;
    if (!alreadyAttempted) {
      await companyStore.ensureGeneralData(cnpj8);
      if (companyStore.companyNotFound) return;
    }

    // Network summary is a separate, heavier query, only fired once access is confirmed
    // above — never checked independently — and only if not already loaded.
    if (companyStore.hasGeneralData && !companyStore.networkStats) {
      await companyStore.fetchNetworkSummary(cnpj8);
    }
  } catch (error) {
    console.error('Error ensuring data for tax id:', error);
  } finally {
    // Log the view regardless of success/failure, but only if we have a CNPJ
    const cnpjToLog = (route.params.cnpj as string || '').slice(0, 8);
    if (cnpjToLog) {
      logView({
        entity_type: 'company',
        entity_id: cnpjToLog,
        entity_label: companyStore.generalData?.razao_social || cnpjToLog
      });
    }
  }
}

onMounted(async () => {
  await getDataForTaxId(taxId.value);
});

// Top-level navigation items
interface NavItem {
  name: string;
  routeName: string;
  activePatterns: string[];
  visible?: boolean;
  count?: number | null;
  countLoading?: boolean;
}

const navItems = computed<NavItem[]>(() => [
  { name: 'Dados Gerais', routeName: 'company-general-headquarters', activePatterns: ['company-general'], visible: true },
  { name: 'KYX', routeName: 'company-kyc-lookup', activePatterns: ['company-kyc'], visible: true },
  { name: 'KYT', routeName: 'company-kyt', activePatterns: ['company-kyt'], visible: hasFeatureAccessOrIsCialUser.value },
  { name: 'Alertas', routeName: 'company-alerts', activePatterns: ['company-alerts'], visible: true },
  { name: 'Investigações', routeName: 'company-investigations', activePatterns: ['company-investigations'], visible: true },
  { name: 'SAR', routeName: 'company-sar', activePatterns: ['company-sar'], visible: true },
  { name: 'Pulse', routeName: 'company-entity-pulse', activePatterns: ['company-entity-pulse'], visible: hasPulseAccess.value, count: pulseEventCount.value, countLoading: pulseEventCountLoading.value },
  { name: 'Outros Dados', routeName: 'company-other-novo-lawsuits', activePatterns: ['company-other'], visible: true },
].filter((item) => item.visible !== false));

const isTabActive = (item: NavItem) => {
  return item.activePatterns.some((pattern: string) => String(route.name).startsWith(pattern));
};

// Sub-navigation for "Dados Gerais"
const generalDataSubItems = computed(() => {
  const items = [
    { name: 'Empresa & HQ Branch', routeName: 'company-general-headquarters' },
    { name: 'Filiais', routeName: 'company-general-branches' },
  ];

  // Only show if more than 1 branch
  if (companyStore.corporateSummary && companyStore.corporateSummary.totalBranches > 1) {
    items.push({ name: 'Visão Consolidada das Filiais', routeName: 'company-general-corporate' });
  }

  items.push(
    { name: 'Relationship tree', routeName: 'company-general-relationship-tree' },
    // { name: 'Faturamento', routeName: 'company-general-revenue' },
    // { name: 'Contrato social', routeName: 'company-general-social-contract' }
  );
  return items;
});

// Sub-navigation for "KYC"
const kycSubItems = [
  { name: 'Lookup', routeName: 'company-kyc-lookup' },
  { name: 'Parecer', routeName: 'company-kyc-assessment' },
];

// Sub-navigation for "Outros Dados"
const otherDataSubItems = [
  { name: 'Processos', routeName: 'company-other-novo-lawsuits' },
  { name: 'Certidões', routeName: 'company-other-certificates' },
  { name: 'Mídia IA', routeName: 'company-other-media-ai' },
];

const showGeneralSubNav = computed(() => String(route.name).startsWith('company-general'));
const showKycSubNav = computed(() => String(route.name).startsWith('company-kyc'));
const showOtherDataSubNav = computed(() => String(route.name).startsWith('company-other'));
const isOtherDataSubActive = (routeName: string) => {
  const routePrefix = routeName.endsWith('s') ? routeName.slice(0, -1) : routeName;
  return String(route.name).startsWith(routePrefix);
};

</script>

<template>
  <div class="company-page-container">
    <section v-if="companyStore.companyNotFound"
      class="mx-auto flex min-h-[70vh] max-w-3xl flex-col justify-center py-8 sm:py-12" aria-labelledby="company-not-found-title">
      <router-link :to="{ name: 'company-lookup' }"
        class="mb-6 inline-flex w-fit items-center gap-2 rounded-md text-sm font-medium text-slate-500 transition-colors hover:text-primary-800 focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-primary-700">
        <BaseIcon size="sm" aria-hidden="true">arrow_back</BaseIcon>
        Consulta de empresas
      </router-link>

      <div class="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
        <div class="h-1 bg-primary-700" aria-hidden="true"></div>
        <div class="p-6 sm:p-10 lg:p-12">
          <div class="mb-8 flex flex-wrap items-center justify-between gap-4">
            <div class="flex h-16 w-16 items-center justify-center rounded-2xl bg-primary-50 text-primary-700 ring-1 ring-inset ring-primary-100">
              <BaseIcon :size="32" aria-hidden="true">domain_disabled</BaseIcon>
            </div>
            <span class="rounded-full bg-slate-100 px-3 py-1 text-xs font-medium text-slate-600">Nenhum resultado</span>
          </div>

          <div role="status">
            <h1 id="company-not-found-title" class="text-2xl font-semibold tracking-tight text-slate-900 sm:text-3xl">
              Empresa não encontrada
            </h1>
            <p class="mt-4 max-w-xl text-base leading-7 text-slate-600">
              Não encontramos uma empresa cadastrada com o CNPJ informado. Confira o identificador e faça uma nova consulta.
            </p>
          </div>

          <div class="mt-7 flex flex-wrap items-center justify-between gap-3 rounded-xl border border-slate-200 bg-slate-50 px-5 py-4">
            <span class="text-sm text-slate-500">Raiz do CNPJ consultado</span>
            <span class="font-mono text-base font-semibold tracking-widest text-slate-800">{{ taxId }}</span>
          </div>

          <router-link :to="{ name: 'company-lookup' }"
            class="mt-8 inline-flex min-h-11 w-full items-center justify-center gap-2 rounded-lg bg-primary-800 px-5 py-3 text-sm font-semibold text-white transition-colors hover:bg-primary-900 focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-primary-700 sm:w-auto">
            <BaseIcon size="sm" aria-hidden="true">search</BaseIcon>
            Consultar outra empresa
          </router-link>
        </div>
      </div>
    </section>
    <template v-else>
    <CompanyBanner :monitoring="monitoringView" />
    <!-- Top Level Navigation -->
    <div class="tab-links">
      <router-link v-for="item in navItems" :key="item.routeName"
        :to="{ name: item.routeName, params: { cnpj: route.params.cnpj } }"
        class="py-3 px-1 no-underline text-gray-600 font-medium text-base border-b-[3px] border-b-transparent transition-colors duration-200 hover:text-blue-900 inline-flex items-center gap-1.5"
        :class="{ '!text-primary-900 !font-semibold !border-b-primary-900': isTabActive(item) }">
        <span>{{ item.name }}</span>
        <span v-if="item.countLoading"
          class="inline-block h-3.5 w-3.5 animate-spin rounded-full border-2 border-surface-300 border-t-primary-600"></span>
        <span v-else-if="item.count"
          class="inline-flex min-w-[1.25rem] items-center justify-center rounded-full bg-primary-100 px-1.5 py-0.5 text-xs font-semibold text-primary-700">
          {{ item.count }}
        </span>
      </router-link>
    </div>

    <!-- Secondary Navigation for Dados Gerais -->
    <div v-if="showGeneralSubNav" class="sub-tab-links">
      <router-link v-for="subItem in generalDataSubItems" :key="subItem.routeName"
        :to="{ name: subItem.routeName, params: { cnpj: route.params.cnpj } }" class="sub-tab-link"
        :class="{ active: $route.name === subItem.routeName }">
        {{ subItem.name }}
      </router-link>
    </div>

    <!-- Secondary Navigation for KYC -->
    <div v-if="showKycSubNav" class="sub-tab-links">
      <router-link v-for="subItem in kycSubItems" :key="subItem.routeName"
        :to="{ name: subItem.routeName, params: { cnpj: route.params.cnpj } }" class="sub-tab-link"
        :class="{ active: $route.name === subItem.routeName }">
        {{ subItem.name }}
      </router-link>
    </div>

    <!-- Secondary Navigation for Outros Dados -->
    <div v-if="showOtherDataSubNav" class="sub-tab-links">
      <router-link v-for="subItem in otherDataSubItems" :key="subItem.routeName"
        :to="{ name: subItem.routeName, params: { cnpj: route.params.cnpj } }" class="sub-tab-link"
        :class="{ active: isOtherDataSubActive(subItem.routeName) }">
        {{ subItem.name }}
      </router-link>
    </div>

    <div class="company-page-card">
      <!-- Tabs with meta.keepAlive survive tab switches; the cache is dropped with the page on entity change. -->
      <router-view v-slot="{ Component, route: tabRoute }">
        <KeepAlive>
          <component :is="Component" v-if="tabRoute.meta.keepAlive" :key="tabRoute.name" />
        </KeepAlive>
        <component :is="Component" v-if="!tabRoute.meta.keepAlive" />
      </router-view>
    </div>
    </template>
  </div>
</template>

<style scoped>
.company-page-container {
  width: 100%;
  height: 100vh;
  padding: 2rem;
  background-color: #f8f9fa;
}

.company-page-card {
  background: #ffffff;
  padding: 1.5rem;
  border-radius: 16px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
  width: 100%;
  margin-top: 1rem;
}

.tab-links {
  display: flex;
  gap: 1.5rem;
  margin-bottom: 0;
  border-bottom: 1px solid #dee2e6;
}

.tab-link {
  padding: 0.75rem 0.25rem;
  text-decoration: none;
  color: #495057;
  background: none;
  border: none;
  font-weight: 500;
  font-size: 1rem;
  border-bottom: 3px solid transparent;
  transition: border-color 0.2s, color 0.2s;
}

.tab-link:hover {
  color: #004d40;
}

.tab-link.active {
  color: #004d40;
  font-weight: 600;
  border-bottom-color: #004d40;
}

.sub-tab-links {
  display: flex;
  gap: 1.25rem;
  margin-top: 0.5rem;
  padding: 0.5rem 0;
}

.sub-tab-link {
  padding: 0.4rem 0.75rem;
  text-decoration: none;
  color: #6c757d;
  background: #f1f3f5;
  border-radius: 20px;
  font-size: 0.875rem;
  font-weight: 500;
  transition: background-color 0.2s, color 0.2s;
}

.sub-tab-link:hover {
  background: var(--p-primary-100);
  color: var(--p-primary-900);
}

.sub-tab-link.active {
  background: var(--p-primary-500);
  color: var(--p-primary-50);
}
</style>
